import { useEffect, useMemo, useRef, useState } from 'react';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import {
  canonicalScopeKey,
  overlayCacheKey,
  readInsightsCache,
  writeInsightsCache,
} from '../api/insightsLocalCache';
import type {
  FunnelFilterOptions,
  FunnelInsightFilters,
  FunnelInsightWarning,
  FunnelOverlayData,
  FunnelScreenAnswersData,
  ScreenAnswersData,
  UserTraitValueOption,
} from '../types';

/**
 * I-B1 (CRO-AC-014 pattern): pre-label D-F1 cache entries stored trait values
 * as plain string arrays — degrade them to label==value options instead of
 * crashing; the next Refresh re-stamps the snapshot with real labels.
 */
export function normalizeUserTraitOptions(
  raw: Record<string, Array<string | UserTraitValueOption>> | null | undefined,
): Record<string, UserTraitValueOption[]> | null {
  if (!raw) return null;
  return Object.fromEntries(Object.entries(raw).map(([key, values]) => [
    key,
    (Array.isArray(values) ? values : []).map(item => (
      typeof item === 'string' ? { value: item, label: item } : item
    )),
  ]));
}

interface UseFunnelOverlayInput {
  projectId: string;
  onboardingId: string;
  versionId: string;
  enabled: boolean;
  filters?: FunnelInsightFilters;
  /**
   * Report window carried over from the Reports dashboard Open action
   * (like-for-like period). Absent = all-time (legacy behavior). Both bounds
   * ride every overlay fetch AND the cache scope key, so a snapshot taken for
   * one period is never served for another.
   */
  from?: string;
  to?: string;
}

interface UseFunnelOverlayResult {
  data: FunnelOverlayData | null;
  filterOptions: FunnelFilterOptions | null;
  userTraitOptions: Record<string, UserTraitValueOption[]> | null;
  /** FU part F (D-F3): per-screen answer/variable distributions — batch-loaded, read synchronously. */
  screenAnswers: Record<string, ScreenAnswersData> | null;
  /** null = the batch endpoint failed (drawer shows a degraded note, never blank). */
  screenAnswersAvailable: boolean;
  loading: boolean;
  error: string | null;
  warnings: FunnelInsightWarning[];
  /** FU part F (D-F1 pattern): payloads came from the local snapshot — zero network. */
  servedFromCache: boolean;
  cachedAt: string | null;
  refresh: () => void;
}

/** Snapshot payloads for the per-(project,funnel) overlay cache entry. */
interface OverlayCachePayloads {
  overlay: FunnelOverlayData | null;
  filterOptions: FunnelFilterOptions | null;
  /** May hold legacy string[] lists when read from a pre-label cache entry. */
  userTraitOptions: Record<string, Array<string | UserTraitValueOption>> | null;
  warnings: FunnelInsightWarning[];
  screenAnswers: FunnelScreenAnswersData | null;
}

export function useFunnelOverlay(input: UseFunnelOverlayInput): UseFunnelOverlayResult {
  const [data, setData] = useState<FunnelOverlayData | null>(null);
  const [filterOptions, setFilterOptions] = useState<FunnelFilterOptions | null>(null);
  const [userTraitOptions, setUserTraitOptions] = useState<Record<string, UserTraitValueOption[]> | null>(null);
  const [screenAnswersData, setScreenAnswersData] = useState<FunnelScreenAnswersData | null>(null);
  const [warnings, setWarnings] = useState<FunnelInsightWarning[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [servedFromCache, setServedFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // The refresh button bypasses the cache exactly ONCE; later re-entries into
  // report mode serve the freshly rewritten snapshot again (FU-AC-018).
  const skipCacheOnceRef = useRef(false);

  const canLoad = input.enabled && Boolean(input.projectId && input.onboardingId && input.versionId);
  const client = useMemo(() => new FunnelInsightsApiClient(), []);
  const filtersKey = JSON.stringify(input.filters ?? {});

  useEffect(() => {
    if (!canLoad) {
      setLoading(false);
      setError(null);
      return;
    }

    const cacheKey = overlayCacheKey(input.projectId, input.onboardingId);
    const scopeKey = canonicalScopeKey({
      onboardingId: input.onboardingId,
      versionId: input.versionId,
      filters: filtersKey,
      from: input.from,
      to: input.to,
    });

    const skipCache = skipCacheOnceRef.current;
    skipCacheOnceRef.current = false;
    if (!skipCache) {
      const entry = readInsightsCache<OverlayCachePayloads>(cacheKey);
      if (entry && entry.scopeKey === scopeKey && entry.payloads.overlay) {
        setData(entry.payloads.overlay);
        setFilterOptions(entry.payloads.filterOptions ?? null);
        setUserTraitOptions(normalizeUserTraitOptions(entry.payloads.userTraitOptions));
        setScreenAnswersData(entry.payloads.screenAnswers ?? null);
        setWarnings(entry.payloads.warnings ?? []);
        setServedFromCache(true);
        setCachedAt(entry.savedAt);
        setLoading(false);
        setError(null);
        return;
      }
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const scope = {
      projectId: input.projectId,
      onboardingId: input.onboardingId,
      versionId: input.versionId,
      from: input.from,
      to: input.to,
      signal: controller.signal,
    };
    // Supplementary payloads degrade independently — the overlay stays usable
    // without trait suggestions or answer distributions.
    const traitsPromise: Promise<Record<string, UserTraitValueOption[]> | null> = client.getUserTraits(scope)
      .then(response => Object.fromEntries(
        response.data.traits.map(trait => [
          trait.key,
          trait.values.map(entry => ({
            value: entry.value,
            label: entry.label || entry.value,
            users: entry.users,
          })),
        ]),
      ))
      .catch(() => null);
    // FU part F (D-F3): ONE batch request for ALL screens of the version.
    const answersPromise: Promise<FunnelScreenAnswersData | null> = client.getScreenAnswers(scope)
      .then(response => response.data)
      .catch(() => null);

    Promise.all([
      client.getFunnelOverlay({ ...scope, filters: input.filters }),
      client.getFilters({ ...scope, filters: input.filters }),
    ]).then(async ([overlayResponse, filterResponse]) => {
      const [traits, answers] = await Promise.all([traitsPromise, answersPromise]);
      if (controller.signal.aborted) return;
      setData(overlayResponse.data);
      setFilterOptions(filterResponse.data);
      setUserTraitOptions(traits);
      setScreenAnswersData(answers);
      setWarnings(overlayResponse.warnings ?? []);
      setServedFromCache(false);
      setCachedAt(null);
      writeInsightsCache<OverlayCachePayloads>(cacheKey, scopeKey, {
        overlay: overlayResponse.data,
        filterOptions: filterResponse.data,
        userTraitOptions: traits,
        warnings: overlayResponse.warnings ?? [],
        screenAnswers: answers,
      });
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load funnel insights');
      setData(null);
      setFilterOptions(null);
      setScreenAnswersData(null);
      setWarnings([]);
      setServedFromCache(false);
      setCachedAt(null);
    }).finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    });

    return () => controller.abort();
  }, [canLoad, client, input.projectId, input.onboardingId, input.versionId, input.from, input.to, filtersKey, refreshKey]);

  const screenAnswers = useMemo(() => {
    if (!screenAnswersData) return null;
    return Object.fromEntries(screenAnswersData.screens.map(screen => [screen.screenId, screen]));
  }, [screenAnswersData]);

  return {
    data,
    filterOptions,
    userTraitOptions,
    screenAnswers,
    screenAnswersAvailable: screenAnswersData !== null,
    loading,
    error,
    warnings,
    servedFromCache,
    cachedAt,
    refresh: () => {
      skipCacheOnceRef.current = true;
      setRefreshKey(key => key + 1);
    },
  };
}
